import React, { useState, useEffect, useContext, useMemo } from "react";
import { BarChart3, FileText, DollarSign, ClipboardList, Download, TrendingUp, TrendingDown, Music, Flame, Users, Search, Calendar, ChevronDown, ChevronUp } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { formatDate } from "../../utils/dateFormat";
import { API, AuthContext } from "../../App";
import axios from "axios";
import { toast } from "sonner";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { addOfficialLetterhead, addReportFooter } from "../../utils/pdfUtils";
import { generateProfitLossPDF, generateOutstandingPaymentsPDF } from "../../utils/adminPdfGenerators";


const CustomerSummarySection = ({ data, loading, onRefresh }) => {
  const [filter, setFilter] = useState('all');
  const [sortBy, setSortBy] = useState('total_desc');
  const [search, setSearch] = useState('');

  const customers = data?.customers || [];

  const filtered = useMemo(() => {
    let list = customers;

    // Status filter
    if (filter === 'has_pending') list = list.filter(c => c.total_pending > 0);
    else if (filter === 'has_completed') list = list.filter(c => c.total_completed > 0);
    else if (filter === 'has_cancelled') list = list.filter(c => c.total_cancelled > 0);
    else if (filter === 'has_unpaid') list = list.filter(c => c.total_unpaid > 0);
    else if (filter === 'limit_reached') list = list.filter(c => c.limit_reached);

    // Search
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(c =>
        c.name?.toLowerCase().includes(q) ||
        c.email?.toLowerCase().includes(q) ||
        c.phone?.includes(q) ||
        c.block_unit?.toLowerCase().includes(q)
      );
    }

    // Sort
    list = [...list].sort((a, b) => {
      switch (sortBy) {
        case 'total_desc': return b.total_bookings - a.total_bookings;
        case 'total_asc': return a.total_bookings - b.total_bookings;
        case 'revenue_desc': return b.total_revenue - a.total_revenue;
        case 'revenue_asc': return a.total_revenue - b.total_revenue;
        case 'name_asc': return (a.name || '').localeCompare(b.name || '');
        case 'name_desc': return (b.name || '').localeCompare(a.name || '');
        case 'recent': return (b.last_booking_date || '').localeCompare(a.last_booking_date || '');
        default: return 0;
      }
    });

    return list;
  }, [customers, filter, search, sortBy]);

  const downloadCSV = () => {
    const headers = ['Name', 'Email', 'Phone', 'Block/Unit', 'Active', 'Limit', 'Karaoke Pending', 'Karaoke Paid', 'Karaoke Completed', 'Karaoke Cancelled', 'Karaoke Revenue', 'BBQ Pending', 'BBQ Paid', 'BBQ Completed', 'BBQ Cancelled', 'BBQ Revenue', 'Total Bookings', 'Total Revenue', 'Last Booking'];
    const rows = filtered.map(c => [
      c.name, c.email, c.phone, c.block_unit, c.active_bookings, c.limit_reached ? 'YES' : '',
      c.karaoke.pending, c.karaoke.paid, c.karaoke.completed, c.karaoke.cancelled, c.karaoke.revenue,
      c.bbq.pending, c.bbq.paid, c.bbq.completed, c.bbq.cancelled, c.bbq.revenue,
      c.total_bookings, c.total_revenue, c.last_booking_date
    ]);
    const csv = [headers, ...rows].map(r => r.map(v => `"${v}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `customer_summary_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadPDF = () => {
    const doc = new jsPDF({ orientation: 'landscape' });
    doc.setFontSize(16);
    doc.text('Customer Booking Summary', 14, 15);
    doc.setFontSize(9);
    doc.text(`Generated: ${new Date().toLocaleString('en-SG')}  |  ${filtered.length} customers`, 14, 22);

    autoTable(doc, {
      startY: 28,
      head: [['Name', 'Email', 'Phone', 'Active', 'Pend', 'Paid', 'Done', 'Unpd', 'Canc', 'Total', 'Revenue', 'Last Booking']],
      body: filtered.map(c => [
        c.name,
        c.email,
        c.phone,
        `${c.active_bookings}/5${c.limit_reached ? ' MAX' : ''}`,
        c.total_pending || '',
        (c.karaoke.paid + c.bbq.paid) || '',
        c.total_completed || '',
        c.total_unpaid || '',
        c.total_cancelled || '',
        c.total_bookings,
        `$${c.total_revenue}`,
        c.last_booking_date
      ]),
      styles: { fontSize: 7, cellPadding: 2 },
      headStyles: { fillColor: [13, 148, 136], fontSize: 7 },
      columnStyles: {
        3: { fontStyle: 'bold' },
      },
      didParseCell: (data) => {
        if (data.section === 'body' && data.column.index === 3) {
          const val = data.cell.raw || '';
          if (val.includes('MAX')) {
            data.cell.styles.textColor = [220, 38, 38];
            data.cell.styles.fontStyle = 'bold';
          }
        }
      }
    });
    doc.save(`customer_summary_${new Date().toISOString().split('T')[0]}.pdf`);
  };

  return (
    <div className="space-y-4">
      <div className="brutal-card">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <h3 className="text-lg font-bold flex items-center gap-2">
            <Users className="text-teal-600" /> Customer Booking Summary
          </h3>
          <div className="flex gap-2">
            <button onClick={onRefresh} disabled={loading} className="brutal-btn-outline text-xs" data-testid="customer-refresh-btn">
              {loading ? 'Loading...' : 'Refresh'}
            </button>
            <button onClick={downloadCSV} disabled={!filtered.length} className="flex items-center gap-1 px-3 py-1.5 bg-teal-600 text-white font-bold text-xs border-2 border-black hover:bg-teal-700" data-testid="customer-download-csv">
              <Download size={14} /> CSV
            </button>
            <button onClick={downloadPDF} disabled={!filtered.length} className="flex items-center gap-1 px-3 py-1.5 bg-slate-700 text-white font-bold text-xs border-2 border-black hover:bg-slate-800" data-testid="customer-download-pdf">
              <Download size={14} /> PDF
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="relative max-w-md mb-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
          <input
            type="text"
            placeholder="Search by name, email, phone, address..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2 border-2 border-black text-sm"
            data-testid="customer-search"
          />
        </div>

        {/* Filters & Sort */}
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center mb-4">
          <div className="flex gap-1 flex-wrap">
            {[
              { key: 'all', label: 'All' },
              { key: 'has_pending', label: 'Has Pending' },
              { key: 'has_completed', label: 'Has Completed' },
              { key: 'has_unpaid', label: 'Has Unpaid' },
              { key: 'has_cancelled', label: 'Has Cancelled' },
              { key: 'limit_reached', label: 'Limit Reached' },
            ].map(f => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                className={`px-2.5 py-1 text-xs font-bold border-2 transition-colors ${
                  filter === f.key ? 'border-teal-500 bg-teal-600 text-white' : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
                }`}
                data-testid={`customer-filter-${f.key}`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-2 py-1 text-xs border-2 border-gray-300 font-bold bg-white"
            data-testid="customer-sort"
          >
            <option value="total_desc">Total Bookings (Most)</option>
            <option value="total_asc">Total Bookings (Least)</option>
            <option value="revenue_desc">Revenue (Highest)</option>
            <option value="revenue_asc">Revenue (Lowest)</option>
            <option value="name_asc">Name (A-Z)</option>
            <option value="name_desc">Name (Z-A)</option>
            <option value="recent">Most Recent Booking</option>
          </select>
          <span className="text-sm text-gray-500 ml-auto">{filtered.length} customer{filtered.length !== 1 ? 's' : ''}</span>
        </div>

        {/* Table */}
        {loading ? (
          <p className="text-center py-8 text-gray-500">Loading customer data...</p>
        ) : filtered.length === 0 ? (
          <p className="text-center py-8 text-gray-500">No customers found</p>
        ) : (
          <div className="overflow-x-auto border-2 border-black">
            <table className="w-full text-xs">
              <thead className="bg-teal-100">
                <tr>
                  <th className="p-2 text-left font-bold">Customer</th>
                  <th className="p-2 text-left font-bold">Contact</th>
                  <th className="p-2 text-center font-bold">Active</th>
                  <th className="p-2 text-center font-bold" title="Pending">Pend</th>
                  <th className="p-2 text-center font-bold" title="Paid">Paid</th>
                  <th className="p-2 text-center font-bold" title="Completed">Done</th>
                  <th className="p-2 text-center font-bold" title="Completed Unpaid">Unpd</th>
                  <th className="p-2 text-center font-bold" title="Cancelled">Canc</th>
                  <th className="p-2 text-center font-bold">Total</th>
                  <th className="p-2 text-right font-bold">Revenue</th>
                  <th className="p-2 text-left font-bold">Last Booking</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c, i) => (
                  <tr key={i} className="border-t hover:bg-teal-50/50">
                    <td className="p-2">
                      <div className="font-bold">{c.name}</div>
                      <div className="text-gray-400 text-[10px]">{c.block_unit}</div>
                    </td>
                    <td className="p-2">
                      <div className="text-gray-600 truncate max-w-[140px]" title={c.email}>{c.email}</div>
                      <div className="text-gray-500">{c.phone}</div>
                    </td>
                    <td className="p-2 text-center">
                      <span className={`px-1.5 py-0.5 rounded font-bold text-[11px] ${
                        c.limit_reached ? 'bg-red-600 text-white' : c.active_bookings >= 4 ? 'bg-amber-200 text-amber-800' : 'bg-gray-100 text-gray-600'
                      }`}>
                        {c.active_bookings}/5{c.limit_reached ? ' MAX' : ''}
                      </span>
                    </td>
                    <td className="p-2 text-center">
                      {(c.karaoke.pending + c.bbq.pending) > 0 && (
                        <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded font-bold">{c.karaoke.pending + c.bbq.pending}</span>
                      )}
                    </td>
                    <td className="p-2 text-center">
                      {(c.karaoke.paid + c.bbq.paid) > 0 && (
                        <span className="px-1.5 py-0.5 bg-green-100 text-green-700 rounded font-bold">{c.karaoke.paid + c.bbq.paid}</span>
                      )}
                    </td>
                    <td className="p-2 text-center">
                      {(c.karaoke.completed + c.bbq.completed) > 0 && (
                        <span className="px-1.5 py-0.5 bg-gray-200 text-gray-700 rounded font-bold">{c.karaoke.completed + c.bbq.completed}</span>
                      )}
                    </td>
                    <td className="p-2 text-center">
                      {(c.karaoke.completed_unpaid + c.bbq.completed_unpaid) > 0 && (
                        <span className="px-1.5 py-0.5 bg-orange-200 text-orange-700 rounded font-bold">{c.karaoke.completed_unpaid + c.bbq.completed_unpaid}</span>
                      )}
                    </td>
                    <td className="p-2 text-center">
                      {(c.karaoke.cancelled + c.bbq.cancelled) > 0 && (
                        <span className="px-1.5 py-0.5 bg-red-100 text-red-700 rounded font-bold">{c.karaoke.cancelled + c.bbq.cancelled}</span>
                      )}
                    </td>
                    <td className="p-2 text-center font-bold">{c.total_bookings}</td>
                    <td className="p-2 text-right font-bold text-green-700">${c.total_revenue}</td>
                    <td className="p-2 text-gray-600">{c.last_booking_date}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

const KJCostDetailTable = ({ entries }) => {
  const [filter, setFilter] = useState('all');
  const filtered = filter === 'all' ? entries : entries.filter(e => {
    if (filter === 'pending') return e.status === 'pending';
    if (filter === 'paid') return e.status === 'paid';
    if (filter === 'completed') return e.status === 'completed' || e.status === 'completed_unpaid';
    return true;
  });
  const filteredTotal = filtered.reduce((sum, e) => sum + e.payout, 0);
  
  return (
    <div className="p-4 border-2 border-black bg-white" data-testid="kj-cost-detail">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
        <p className="text-sm font-bold">KJ Cost Detail ({filtered.length} session{filtered.length !== 1 ? 's' : ''} = ${filteredTotal})</p>
        <div className="flex gap-1 flex-wrap">
          {['all', 'pending', 'paid', 'completed'].map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-2.5 py-1 text-xs font-bold border-2 transition-colors ${
                filter === f ? 'border-red-500 bg-red-600 text-white' : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
              }`}
              data-testid={`kj-filter-${f}`}
            >
              {f === 'all' ? 'All' : f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
      </div>
      <div className="overflow-x-auto max-h-64 overflow-y-auto border border-gray-300">
        <table className="w-full text-xs">
          <thead className="bg-red-50 sticky top-0">
            <tr>
              <th className="p-2 text-left font-bold">Code</th>
              <th className="p-2 text-left font-bold">KJ Name</th>
              <th className="p-2 text-left font-bold">Date</th>
              <th className="p-2 text-left font-bold">Slot</th>
              <th className="p-2 text-left font-bold">Status</th>
              <th className="p-2 text-left font-bold">Type</th>
              <th className="p-2 text-right font-bold">Payout</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((e, i) => (
              <tr key={i} className="border-t border-gray-200 hover:bg-red-50/50">
                <td className="p-2 font-mono text-purple-600">{e.booking_code}</td>
                <td className="p-2 font-bold">{e.kj_name}</td>
                <td className="p-2">{e.date}</td>
                <td className="p-2">{e.slot_type === 'afternoon' ? '1PM' : '6PM'}</td>
                <td className="p-2">
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
                    e.status === 'paid' ? 'bg-green-100 text-green-700' :
                    e.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                    e.status === 'completed' ? 'bg-gray-200 text-gray-700' :
                    e.status === 'completed_unpaid' ? 'bg-orange-200 text-orange-700' :
                    'bg-gray-100 text-gray-600'
                  }`}>
                    {e.status === 'completed_unpaid' ? 'UNPAID' : e.status?.toUpperCase()}
                  </span>
                </td>
                <td className="p-2">{e.kj_type}</td>
                <td className="p-2 text-right font-bold text-red-600">{e.payout > 0 ? `$${e.payout}` : '-'}</td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="p-4 text-center text-gray-400">No KJ sessions for this filter</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const SectionTab = ({ active, onClick, icon: Icon, label, color }) => (
  <button
    onClick={onClick}
    data-testid={`finance-section-${label.toLowerCase().replace(/\s/g, '-')}`}
    className={`flex items-center gap-2 px-5 py-3 font-bold text-sm border-2 border-black transition-all ${
      active
        ? `${color} text-white shadow-[3px_3px_0px_0px_rgba(0,0,0,1)]`
        : 'bg-white hover:bg-gray-50'
    }`}
  >
    <Icon size={18} />
    {label}
  </button>
);

const AdminFinanceTab = ({
  annualYear,
  setAnnualYear,
  annualSummary,
  loadingAnnual,
  onFetchAnnualSummary,
  reportMonth,
  setReportMonth,
  reportYear,
  setReportYear,
  centralisedReport,
  loadingReport,
  onFetchCentralisedReport,
  onGenerateCentralisedPDF,
  confirmedBookings,
  pendingBookings,
  bookings,
}) => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [activeSection, setActiveSection] = useState('income');

  // KJ Allowance state
  const [kjReportMonth, setKjReportMonth] = useState(new Date().getMonth() + 1);
  const [kjReportYear, setKjReportYear] = useState(new Date().getFullYear());
  const [kjReportFilter, setKjReportFilter] = useState("");
  const [kjReport, setKjReport] = useState(null);
  const [loadingKjReport, setLoadingKjReport] = useState(false);

  // Reconciliation state
  const [reconciliationData, setReconciliationData] = useState(null);
  const [loadingRecon, setLoadingRecon] = useState(false);

  // BBQ Report state
  const [bbqReportMonth, setBbqReportMonth] = useState(new Date().getMonth() + 1);
  const [bbqReportYear, setBbqReportYear] = useState(new Date().getFullYear());
  const [bbqReport, setBbqReport] = useState(null);
  const [loadingBbq, setLoadingBbq] = useState(false);

  // Monthly Settlement state
  const [settlementMonth, setSettlementMonth] = useState(new Date().getMonth() + 1);
  const [settlementYear, setSettlementYear] = useState(new Date().getFullYear());
  const [settlementData, setSettlementData] = useState(null);
  const [loadingSettlement, setLoadingSettlement] = useState(false);
  const [settlementKj, setSettlementKj] = useState("");
  const [kjList, setKjList] = useState([]);

  // Yearly Summary state
  const [yearlySummaryYear, setYearlySummaryYear] = useState(new Date().getFullYear());
  const [yearlyData, setYearlyData] = useState(null);
  const [loadingYearly, setLoadingYearly] = useState(false);

  // BBQ Yearly Summary state
  const [bbqYearlyYear, setBbqYearlyYear] = useState(new Date().getFullYear());
  const [bbqYearlyData, setBbqYearlyData] = useState(null);
  const [loadingBbqYearly, setLoadingBbqYearly] = useState(false);

  // P/L Dashboard state
  const [plMonth, setPlMonth] = useState(new Date().getMonth() + 1);
  const [plYear, setPlYear] = useState(new Date().getFullYear());
  const [plData, setPlData] = useState(null);
  const [loadingPl, setLoadingPl] = useState(false);
  const [plAnnualYear, setPlAnnualYear] = useState(new Date().getFullYear());
  const [plAnnual, setPlAnnual] = useState(null);
  const [loadingPlAnnual, setLoadingPlAnnual] = useState(false);

  // Outstanding Payments state
  const [outstandingData, setOutstandingData] = useState(null);
  const [loadingOutstanding, setLoadingOutstanding] = useState(false);

  // Customer Summary state
  const [customerData, setCustomerData] = useState(null);
  const [loadingCustomers, setLoadingCustomers] = useState(false);

  // KJ Payment Tracker state
  const [kjPaymentData, setKjPaymentData] = useState(null);
  const [loadingKjPayment, setLoadingKjPayment] = useState(false);

  // Expandable sections in Karaoke tab
  const [expandedKaraokeSection, setExpandedKaraokeSection] = useState('kj-allowance');

  const monthNames = ["January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"];
  const monthShort = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

  // Fetch KJ Allowance Report
  const fetchKjReport = async () => {
    setLoadingKjReport(true);
    try {
      let url = `${API}/admin/finance/kj-allowance-report?year=${kjReportYear}&month=${kjReportMonth}`;
      if (kjReportFilter) {
        url += `&kj_name=${encodeURIComponent(kjReportFilter)}`;
      }
      const res = await axios.get(url);
      setKjReport(res.data);
    } catch (error) {
      toast.error("Failed to fetch KJ allowance report");
    } finally {
      setLoadingKjReport(false);
    }
  };

  // Fetch Reconciliation
  const fetchReconciliation = async () => {
    setLoadingRecon(true);
    try {
      const res = await axios.get(`${API}/admin/finance/pending-reconciliation`);
      setReconciliationData(res.data);
    } catch (error) {
      toast.error("Failed to fetch reconciliation data");
    } finally {
      setLoadingRecon(false);
    }
  };

  // Fetch BBQ Report - includes paid + completed (income recorded at payment verification)
  const fetchBbqReport = async () => {
    setLoadingBbq(true);
    try {
      // Fetch both paid and completed BBQ bookings
      const [paidRes, completedRes] = await Promise.allSettled([
        axios.get(`${API}/admin/bbq/bookings?page_size=500&status_filter=paid`),
        axios.get(`${API}/admin/bbq/bookings?page_size=500&status_filter=completed`)
      ]);
      
      const paidBookings = paidRes.status === 'fulfilled' 
        ? (Array.isArray(paidRes.value.data?.bookings) ? paidRes.value.data.bookings : [])
        : [];
      const completedBookings = completedRes.status === 'fulfilled'
        ? (Array.isArray(completedRes.value.data?.bookings) ? completedRes.value.data.bookings : [])
        : [];
      
      const allBookings = [...paidBookings, ...completedBookings];

      // Filter by selected month/year
      const startDate = `${bbqReportYear}-${String(bbqReportMonth).padStart(2, '0')}-01`;
      const endMonth = bbqReportMonth === 12 ? 1 : bbqReportMonth + 1;
      const endYear = bbqReportMonth === 12 ? bbqReportYear + 1 : bbqReportYear;
      const endDate = `${endYear}-${String(endMonth).padStart(2, '0')}-01`;

      const filtered = allBookings.filter(b =>
        b.date >= startDate && b.date < endDate && (b.status === 'completed' || b.status === 'paid')
      );

      const total = filtered.reduce((sum, b) => sum + (b.price || 0), 0);
      setBbqReport({ bookings: filtered, total, count: filtered.length });
    } catch (error) {
      toast.error("Failed to fetch BBQ report");
    } finally {
      setLoadingBbq(false);
    }
  };

  // Load reconciliation when Karaoke section opens
  useEffect(() => {
    if (activeSection === 'karaoke' && !reconciliationData) {
      fetchReconciliation();
    }
    if (activeSection === 'karaoke' && kjList.length === 0) {
      fetchKjList();
    }
    if (activeSection === 'pl' && !plData) {
      fetchPl();
    }
    if (activeSection === 'pl' && !plAnnual) {
      fetchPlAnnual();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSection]);

  // Fetch P/L Monthly
  const fetchPl = async () => {
    setLoadingPl(true);
    try {
      const res = await axios.get(`${API}/admin/finance/profit-loss?year=${plYear}&month=${plMonth}`);
      setPlData(res.data);
    } catch { toast.error("Failed to fetch P/L data"); }
    finally { setLoadingPl(false); }
  };

  // Fetch P/L Annual
  const fetchPlAnnual = async () => {
    setLoadingPlAnnual(true);
    try {
      const res = await axios.get(`${API}/admin/finance/profit-loss-annual?year=${plAnnualYear}`);
      setPlAnnual(res.data);
    } catch { toast.error("Failed to fetch annual P/L"); }
    finally { setLoadingPlAnnual(false); }
  };

  // Fetch Outstanding Payments
  const fetchOutstanding = async () => {
    setLoadingOutstanding(true);
    try {
      const res = await axios.get(`${API}/admin/finance/outstanding-payments`);
      setOutstandingData(res.data);
    } catch { toast.error("Failed to fetch outstanding payments"); }
    finally { setLoadingOutstanding(false); }
  };

  // Fetch Customer Summary
  const fetchCustomerSummary = async () => {
    setLoadingCustomers(true);
    try {
      const res = await axios.get(`${API}/admin/finance/customer-summary`);
      setCustomerData(res.data);
    } catch { toast.error("Failed to fetch customer summary"); }
    finally { setLoadingCustomers(false); }
  };

  // Fetch KJ Payment Tracker
  const fetchKjPayments = async () => {
    setLoadingKjPayment(true);
    try {
      const res = await axios.get(`${API}/admin/finance/kj-payment-tracker`);
      setKjPaymentData(res.data);
    } catch { toast.error("Failed to fetch KJ payment data"); }
    finally { setLoadingKjPayment(false); }
  };

  // Fetch KJ list for settlement filter
  const fetchKjList = async () => {
    try {
      const res = await axios.get(`${API}/admin/finance/kj-list`);
      setKjList(res.data.kj_list || []);
    } catch (error) {
      console.error("Failed to fetch KJ list");
    }
  };

  // Fetch Monthly Settlement Report
  const fetchSettlement = async () => {
    setLoadingSettlement(true);
    try {
      let url = `${API}/admin/finance/monthly-report?year=${settlementYear}&month=${settlementMonth}`;
      if (settlementKj) {
        url += `&kj_name=${encodeURIComponent(settlementKj)}`;
      }
      const res = await axios.get(url);
      setSettlementData(res.data);
    } catch (error) {
      toast.error("Failed to fetch settlement report");
    } finally {
      setLoadingSettlement(false);
    }
  };

  // Fetch Yearly Summary
  const fetchYearlySummary = async () => {
    setLoadingYearly(true);
    try {
      const res = await axios.get(`${API}/admin/finance/yearly-summary?year=${yearlySummaryYear}`);
      setYearlyData(res.data);
    } catch (error) {
      toast.error("Failed to fetch yearly summary");
    } finally {
      setLoadingYearly(false);
    }
  };

  // Fetch BBQ Yearly Summary
  const fetchBbqYearlySummary = async () => {
    setLoadingBbqYearly(true);
    try {
      const res = await axios.get(`${API}/admin/finance/bbq-yearly-summary?year=${bbqYearlyYear}`);
      setBbqYearlyData(res.data);
    } catch (error) {
      toast.error("Failed to fetch BBQ yearly summary");
    } finally {
      setLoadingBbqYearly(false);
    }
  };

  // Generate Settlement PDF
  const generateSettlementPDF = () => {
    if (!settlementData) return;
    const doc = new jsPDF();
    const monthName = settlementData.month_name;
    const year = settlementData.year;

    const contentStartY = addOfficialLetterhead(doc, "KARAOKE BOOKING FINANCIAL REPORT", `${monthName} ${year}`, { r: 0, g: 51, b: 102 });

    let currentY = contentStartY + 1;

    // Summary
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text(`Total Gross Collections: $${settlementData.summary.total_gross}`, 14, currentY);
    currentY += 6;
    doc.text(`Modification Refunds: -$${settlementData.summary.total_refunds || 0}`, 14, currentY);
    currentY += 6;
    doc.setTextColor(220, 38, 38);
    doc.text(`Cancellations/Refunds: -$${settlementData.summary.total_cancellation_refunds || 0}`, 14, currentY);
    currentY += 6;
    doc.setTextColor(0, 128, 0);
    doc.setFont("helvetica", "bold");
    doc.text(`Net Revenue: $${settlementData.summary.net_revenue}`, 14, currentY);
    doc.setTextColor(0, 0, 0);
    doc.setFont("helvetica", "normal");
    currentY += 6;
    doc.text(`Total Bookings: ${settlementData.summary.total_bookings} | Cancelled: ${settlementData.summary.total_cancelled || 0}`, 14, currentY);
    if (settlementKj) {
      currentY += 6;
      doc.text(`Filtered by KJ: ${settlementKj}`, 14, currentY);
    }

    // Transaction Table
    currentY += 10;
    const tableData = settlementData.transactions.map(t => [
      t.date,
      t.resident_name || t.kj_name,
      t.kj_name || '-',
      t.booking_code,
      t.slot_type === 'afternoon' ? '1PM-5PM' : '6PM-10PM',
      `$${t.final_amount}`,
      t.price_difference ? (t.price_difference > 0 ? `+$${t.price_difference}` : `$${t.price_difference}`) : '-',
      t.transaction_number || '-'
    ]);

    autoTable(doc, {
      startY: currentY,
      head: [['Date', 'Resident', 'KJ', 'Code', 'Slot', 'Amount', 'Adj.', 'onePA Txn#']],
      body: tableData,
      theme: 'grid',
      headStyles: { fillColor: [0, 51, 102], textColor: 255 },
      styles: { fontSize: 7 },
      columnStyles: {
        0: { cellWidth: 20 }, 1: { cellWidth: 25 }, 2: { cellWidth: 22 },
        3: { cellWidth: 18 }, 4: { cellWidth: 18 }, 5: { cellWidth: 15 },
        6: { cellWidth: 14 }, 7: { cellWidth: 25 }
      },
      margin: { bottom: 30 }
    });

    currentY = doc.lastAutoTable.finalY + 8;

    // Cancellations table
    if (settlementData.refund_transactions && settlementData.refund_transactions.length > 0) {
      if (currentY > 230) { doc.addPage(); currentY = 20; }
      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(220, 38, 38);
      doc.text("Cancellations / Full Refunds", 14, currentY);
      doc.setTextColor(0, 0, 0);

      const refundData = settlementData.refund_transactions.map(t => [
        t.date, t.resident_name || t.kj_name, t.kj_name || '-',
        t.booking_code, t.slot_type === 'afternoon' ? '1PM-5PM' : '6PM-10PM',
        `$${t.original_price}`, `-$${t.refund_amount}`
      ]);

      autoTable(doc, {
        startY: currentY + 5,
        head: [['Date', 'Resident', 'KJ', 'Code', 'Slot', 'Original', 'Refund']],
        body: refundData,
        theme: 'grid',
        headStyles: { fillColor: [220, 38, 38], textColor: 255 },
        styles: { fontSize: 8 },
        margin: { bottom: 30 }
      });
    }

    // Footer
    addReportFooter(doc, { r: 0, g: 51, b: 102 });

    doc.save(`YCK_Settlement_${monthName}_${year}.pdf`);
    toast.success(`Downloaded settlement report`);
  };

  // Generate KJ Allowance PDF
  const generateKjAllowancePDF = () => {
    if (!kjReport) return;
    const doc = new jsPDF();

    const contentStartY = addOfficialLetterhead(doc, "KJ ALLOWANCE REPORT", `${monthNames[kjReportMonth - 1]} ${kjReportYear}`, { r: 128, g: 0, b: 128 });

    let currentY = contentStartY - 4;

    // Per-KJ Summary Table
    if (kjReport.kj_summary && kjReport.kj_summary.length > 0) {
      doc.setFontSize(11);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(80, 0, 80);
      doc.text("KJ Summary", 14, currentY);
      currentY += 2;

      const summaryData = kjReport.kj_summary.map(kj => [
        kj.kj_name,
        kj.status_tag,
        `${kj.sessions}`,
        `$${kj.total_payout}`
      ]);
      // Add total row
      summaryData.push([
        'TOTAL',
        '',
        `${kjReport.total_sessions}`,
        `$${kjReport.total_to_disburse}`
      ]);

      autoTable(doc, {
        startY: currentY,
        head: [['KJ Name', 'Type', 'Sessions', 'Total Payout']],
        body: summaryData,
        theme: 'grid',
        headStyles: { fillColor: [128, 0, 128], textColor: 255, fontStyle: 'bold' },
        styles: { fontSize: 9, cellPadding: 3 },
        columnStyles: {
          0: { fontStyle: 'bold' },
          2: { halign: 'center' },
          3: { halign: 'right', fontStyle: 'bold' }
        },
        didParseCell: (data) => {
          // Bold the total row
          if (data.row.index === summaryData.length - 1) {
            data.cell.styles.fontStyle = 'bold';
            data.cell.styles.fillColor = [245, 240, 255];
          }
        }
      });

      currentY = doc.lastAutoTable.finalY + 10;
    }

    // Session Details Table
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(80, 0, 80);
    doc.text("Session Details", 14, currentY);
    currentY += 2;

    const tableData = kjReport.entries.map(e => {
      let dateFormatted = e.date;
      try {
        const d = new Date(e.date + 'T00:00:00');
        dateFormatted = d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
      } catch { /* ignore date parse error */ }
      const paymentLabel = e.payout === 0 ? 'N/A' : (e.kj_payment_status || 'pending').toUpperCase();
      return [
        dateFormatted,
        e.kj_name,
        e.booking_code,
        e.slot_time,
        e.status_tag,
        `$${e.payout}`,
        paymentLabel
      ];
    });
    // Grand total row
    const grandTotal = kjReport.entries.reduce((sum, e) => sum + e.payout, 0);
    tableData.push(['', '', '', '', 'TOTAL', `$${grandTotal}`, '']);

    autoTable(doc, {
      startY: currentY,
      head: [['Date', 'KJ Name', 'Code', 'Slot', 'KJ Type', 'Payout', 'KJ Payment']],
      body: tableData,
      theme: 'grid',
      headStyles: { fillColor: [128, 0, 128], textColor: 255 },
      styles: { fontSize: 7 },
      margin: { bottom: 30 },
      didParseCell: (data) => {
        if (data.row.index === tableData.length - 1) {
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fillColor = [245, 240, 255];
        }
        // Color KJ Payment column
        if (data.section === 'body' && data.column.index === 6) {
          if (data.cell.raw === 'PAID') {
            data.cell.styles.textColor = [0, 128, 0];
            data.cell.styles.fontStyle = 'bold';
          } else if (data.cell.raw === 'PENDING') {
            data.cell.styles.textColor = [200, 150, 0];
            data.cell.styles.fontStyle = 'bold';
          } else if (data.cell.raw === 'N/A') {
            data.cell.styles.textColor = [150, 150, 150];
          }
        }
      }
    });

    const pageHeight = doc.internal.pageSize.height;
    doc.setDrawColor(128, 0, 128);
    doc.setLineWidth(0.3);
    doc.line(15, pageHeight - 25, 195, pageHeight - 25);
    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    doc.setFont("helvetica", "italic");
    doc.text("This is a computer-generated document. No signature is required.", 105, pageHeight - 18, { align: "center" });
    doc.setFont("helvetica", "normal");
    doc.text(`Generated: ${new Date().toLocaleString('en-SG', { timeZone: 'Asia/Singapore' })} (Singapore Time)`, 105, pageHeight - 12, { align: "center" });

    const kjNameSuffix = kjReportFilter ? kjReportFilter.replace(/\s+/g, '_') : 'All';
    doc.save(`KJ_Allowance_${monthNames[kjReportMonth - 1]}_${kjReportYear}_${kjNameSuffix}.pdf`);
    toast.success("KJ Allowance PDF downloaded");
  };

  // Generate BBQ Report PDF
  const generateBbqPDF = () => {
    if (!bbqReport) return;
    const doc = new jsPDF();

    const contentStartY = addOfficialLetterhead(doc, "BBQ PIT BOOKING INCOME REPORT", `${monthNames[bbqReportMonth - 1]} ${bbqReportYear}`, { r: 234, g: 88, b: 12 });

    // Summary box
    doc.setFillColor(255, 247, 237);
    doc.rect(15, contentStartY, 180, 18, 'F');
    doc.setDrawColor(200, 200, 200);
    doc.rect(15, contentStartY, 180, 18);
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text("Total BBQ Revenue", 55, contentStartY + 7, { align: "center" });
    doc.text("Total Bookings", 150, contentStartY + 7, { align: "center" });
    doc.setFontSize(12);
    doc.setTextColor(234, 88, 12);
    doc.setFont("helvetica", "bold");
    doc.text(`$${bbqReport.total.toFixed(2)}`, 55, contentStartY + 15, { align: "center" });
    doc.text(`${bbqReport.count}`, 150, contentStartY + 15, { align: "center" });

    const tableData = bbqReport.bookings.map(b => [
      formatDate(b.date) || b.date,
      b.booking_code || '',
      b.name || '',
      b.pit_selection === 'both' ? 'Both Pits' : b.pit_selection === 'pit1' ? 'Pit 1' : b.pit_selection === 'pit2' ? 'Pit 2' : b.pit_number || '-',
      b.slot_type === 'afternoon' ? '1PM-5PM' : '6PM-10PM',
      `$${Number(b.price || 0).toFixed(2)}`,
      b.bank_transfer_reference || '-'
    ]);

    if (tableData.length > 0) {
      autoTable(doc, {
        startY: contentStartY + 26,
        head: [['Date', 'Code', 'Name', 'Pit', 'Slot', 'Amount', 'Bank Ref.']],
        body: tableData,
        styles: { fontSize: 8, cellPadding: 3 },
        headStyles: { fillColor: [234, 88, 12], textColor: [255, 255, 255] },
        alternateRowStyles: { fillColor: [255, 247, 237] }
      });
    }

    addReportFooter(doc, { r: 234, g: 88, b: 12 });

    doc.save(`BBQ_Income_${monthNames[bbqReportMonth - 1]}_${bbqReportYear}.pdf`);
    toast.success("BBQ Income PDF downloaded");
  };

  return (
    <div className="space-y-6">
      {/* Section Tabs */}
      <div className="flex gap-2 flex-wrap" data-testid="finance-section-tabs">
        <SectionTab
          active={activeSection === 'income'}
          onClick={() => setActiveSection('income')}
          icon={DollarSign}
          label="Income Collected"
          color="bg-green-600"
        />
        <SectionTab
          active={activeSection === 'karaoke'}
          onClick={() => setActiveSection('karaoke')}
          icon={Music}
          label="Karaoke Booking"
          color="bg-purple-600"
        />
        <SectionTab
          active={activeSection === 'bbq'}
          onClick={() => setActiveSection('bbq')}
          icon={Flame}
          label="BBQ Pit Booking"
          color="bg-orange-600"
        />
        <SectionTab
          active={activeSection === 'outstanding'}
          onClick={() => { setActiveSection('outstanding'); if (!outstandingData) fetchOutstanding(); }}
          icon={ClipboardList}
          label="Outstanding"
          color="bg-red-600"
        />
        <SectionTab
          active={activeSection === 'pl'}
          onClick={() => setActiveSection('pl')}
          icon={TrendingUp}
          label="P/L Dashboard"
          color="bg-slate-800"
        />
        <SectionTab
          active={activeSection === 'customers'}
          onClick={() => { setActiveSection('customers'); if (!customerData) fetchCustomerSummary(); }}
          icon={Users}
          label="Customers"
          color="bg-teal-600"
        />
      </div>

      {/* ============ SECTION 1: INCOME COLLECTED ============ */}
      {activeSection === 'income' && (
        <div className="space-y-6">
          {/* Monthly Income Report */}
          <div className="brutal-card">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
              <h3 className="text-lg font-bold flex items-center gap-2">
                <FileText className="text-green-600" /> Monthly Income Report
              </h3>
              <div className="flex items-center gap-2 flex-wrap">
                <select value={reportMonth} onChange={(e) => setReportMonth(parseInt(e.target.value))}
                  className="border-2 border-black px-3 py-2 font-bold" data-testid="income-month-select">
                  {monthShort.map((m, i) => (
                    <option key={i} value={i + 1}>{m}</option>
                  ))}
                </select>
                <select value={reportYear} onChange={(e) => setReportYear(parseInt(e.target.value))}
                  className="border-2 border-black px-3 py-2 font-bold" data-testid="income-year-select">
                  {[2024, 2025, 2026, 2027].map(y => (<option key={y} value={y}>{y}</option>))}
                </select>
                <button onClick={onFetchCentralisedReport} disabled={loadingReport}
                  className="px-4 py-2 bg-green-600 text-white font-bold border-2 border-black hover:bg-green-700"
                  data-testid="income-generate-btn">
                  {loadingReport ? 'Loading...' : 'Generate'}
                </button>
              </div>
            </div>

            {centralisedReport && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="p-4 bg-purple-50 border-2 border-purple-300 text-center">
                    <p className="text-xs text-purple-600 font-medium">Karaoke Revenue</p>
                    <p className="text-xl font-bold text-purple-700">${centralisedReport.karaoke_total}</p>
                    <p className="text-xs text-gray-500">{centralisedReport.karaoke_count} bookings</p>
                  </div>
                  <div className="p-4 bg-orange-50 border-2 border-orange-300 text-center">
                    <p className="text-xs text-orange-600 font-medium notranslate" translate="no">BBQ Revenue</p>
                    <p className="text-xl font-bold text-orange-700">${centralisedReport.bbq_total}</p>
                    <p className="text-xs text-gray-500">{centralisedReport.bbq_count} bookings</p>
                  </div>
                  <div className="p-4 bg-green-50 border-2 border-green-300 text-center">
                    <p className="text-xs text-green-600 font-medium">Grand Total</p>
                    <p className="text-xl font-bold text-green-700">${centralisedReport.grand_total}</p>
                    <p className="text-xs text-gray-500">{centralisedReport.karaoke_count + centralisedReport.bbq_count} bookings</p>
                  </div>
                </div>

                {centralisedReport.bookings.length > 0 && (
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[800px] text-sm">
                      <thead>
                        <tr className="bg-gray-100 border-b-2 border-black">
                          <th className="p-2 text-left">Booking Date</th>
                          <th className="p-2 text-left">Txn Date</th>
                          <th className="p-2 text-left">Facility</th>
                          <th className="p-2 text-left">Code</th>
                          <th className="p-2 text-left">Name</th>
                          <th className="p-2 text-left">onePA Txn#</th>
                          <th className="p-2 text-right">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {centralisedReport.bookings.map((b, idx) => (
                          <tr key={idx} className="border-b hover:bg-gray-50">
                            <td className="p-2 font-mono">{formatDate(b.date)}</td>
                            <td className="p-2 font-mono text-xs">{b.transaction_date ? new Date(b.transaction_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}</td>
                            <td className="p-2">
                              <span className={`px-2 py-0.5 text-xs font-bold rounded ${
                                b.facility === 'Karaoke' ? 'bg-purple-100 text-purple-700' : 'bg-orange-100 text-orange-700'
                              }`}>{b.facility}</span>
                            </td>
                            <td className="p-2 font-mono">{b.booking_code}</td>
                            <td className="p-2">{b.resident_name || b.name}</td>
                            <td className="p-2">
                              {b.transaction_number ? (
                                <span className="font-mono text-xs bg-green-50 text-green-700 px-1 py-0.5 rounded">{b.transaction_number}</span>
                              ) : <span className="text-xs text-gray-400">-</span>}
                            </td>
                            <td className="p-2 text-right font-bold">${b.amount || b.price || 0}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                <button onClick={onGenerateCentralisedPDF}
                  className="w-full sm:w-auto px-6 py-3 bg-blue-600 text-white font-bold border-2 border-black hover:bg-blue-700 flex items-center justify-center gap-2"
                  data-testid="income-pdf-btn">
                  <Download size={18} /> EXPORT TO PDF
                </button>
              </div>
            )}

            {!centralisedReport && (
              <p className="text-center text-gray-500 py-8">Select month/year and click &quot;Generate&quot; to view combined income report</p>
            )}
          </div>

          {/* Annual Summary */}
          <div className="brutal-card bg-gradient-to-r from-indigo-50 to-purple-50">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
              <h3 className="text-xl font-bold flex items-center gap-2">
                <BarChart3 className="text-indigo-600" /> Annual Performance Summary
              </h3>
              <div className="flex items-center gap-2">
                <select value={annualYear} onChange={(e) => setAnnualYear(parseInt(e.target.value))}
                  className="border-2 border-black px-3 py-2 font-bold" data-testid="annual-year-select">
                  {[2024, 2025, 2026, 2027].map(y => (<option key={y} value={y}>{y}</option>))}
                </select>
                <button onClick={onFetchAnnualSummary} disabled={loadingAnnual}
                  className="px-4 py-2 bg-indigo-600 text-white font-bold border-2 border-black hover:bg-indigo-700"
                  data-testid="annual-load-btn">
                  {loadingAnnual ? 'Loading...' : 'Load'}
                </button>
              </div>
            </div>

            {annualSummary && (
              <div className="space-y-6">
                {/* Facility Popularity */}
                <div className="bg-white border-2 border-black p-4">
                  <h4 className="font-bold text-sm mb-3 flex items-center gap-2">
                    <TrendingUp size={16} /> Facility Popularity (Total Bookings)
                  </h4>
                  <div className="flex flex-col sm:flex-row gap-4">
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-bold text-purple-700">Karaoke</span>
                        <span className="text-sm">{annualSummary.summary.total_karaoke_bookings} bookings ({annualSummary.facility_comparison.karaoke_percentage}%)</span>
                      </div>
                      <div className="h-6 bg-gray-200 rounded overflow-hidden">
                        <div className="h-full bg-purple-500 transition-all" style={{ width: `${annualSummary.facility_comparison.karaoke_percentage}%` }}></div>
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-bold text-orange-600 notranslate" translate="no">BBQ Pits</span>
                        <span className="text-sm">{annualSummary.summary.total_bbq_bookings} bookings ({annualSummary.facility_comparison.bbq_percentage}%)</span>
                      </div>
                      <div className="h-6 bg-gray-200 rounded overflow-hidden">
                        <div className="h-full bg-orange-500 transition-all" style={{ width: `${annualSummary.facility_comparison.bbq_percentage}%` }}></div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Revenue Bar Chart */}
                <div className="bg-white border-2 border-black p-4">
                  <h4 className="font-bold text-sm mb-3">Monthly Revenue ({annualYear})</h4>
                  <div className="overflow-x-auto">
                    <div className="flex items-end gap-1 min-w-[500px] h-48">
                      {annualSummary.monthly_data.map(m => {
                        const maxRevenue = Math.max(...annualSummary.monthly_data.map(x => x.total_revenue), 1);
                        const karaokeHeight = (m.karaoke_revenue / maxRevenue) * 100;
                        const bbqHeight = (m.bbq_revenue / maxRevenue) * 100;
                        return (
                          <div key={m.month} className="flex-1 flex flex-col items-center">
                            <div className="w-full flex flex-col items-center justify-end" style={{ height: '160px' }}>
                              {m.total_revenue > 0 && <span className="text-xs font-bold mb-1">${m.total_revenue}</span>}
                              <div className="w-full flex flex-col">
                                <div className="w-full bg-orange-400" style={{ height: `${bbqHeight}%`, minHeight: bbqHeight > 0 ? '4px' : '0' }}></div>
                                <div className="w-full bg-purple-500" style={{ height: `${karaokeHeight}%`, minHeight: karaokeHeight > 0 ? '4px' : '0' }}></div>
                              </div>
                            </div>
                            <span className="text-xs font-bold mt-1">{m.month_name}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  <div className="flex justify-center gap-4 mt-3 text-xs">
                    <span className="flex items-center gap-1"><span className="w-3 h-3 bg-purple-500 inline-block"></span> Karaoke</span>
                    <span className="flex items-center gap-1"><span className="w-3 h-3 bg-orange-400 inline-block"></span> BBQ</span>
                  </div>
                </div>

                {/* YTD Summary */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="text-center p-4 bg-purple-100 border-2 border-purple-400">
                    <p className="text-2xl font-bold text-purple-700">${annualSummary.summary.total_karaoke_revenue}</p>
                    <p className="text-xs text-purple-600 font-medium">Karaoke YTD</p>
                  </div>
                  <div className="text-center p-4 bg-orange-100 border-2 border-orange-400">
                    <p className="text-2xl font-bold text-orange-700">${annualSummary.summary.total_bbq_revenue}</p>
                    <p className="text-xs text-orange-600 font-medium">BBQ YTD</p>
                  </div>
                  <div className="text-center p-4 bg-green-100 border-2 border-green-400">
                    <p className="text-2xl font-bold text-green-700">${annualSummary.summary.grand_total_revenue}</p>
                    <p className="text-xs text-green-600 font-medium">Total Revenue</p>
                  </div>
                  <div className="text-center p-4 bg-blue-100 border-2 border-blue-400">
                    <p className="text-2xl font-bold text-blue-700">{annualSummary.summary.grand_total_bookings}</p>
                    <p className="text-xs text-blue-600 font-medium">Total Bookings</p>
                  </div>
                </div>
              </div>
            )}

            {!annualSummary && (
              <p className="text-center text-gray-500 py-8">Click &quot;Load&quot; to view annual performance data</p>
            )}
          </div>
        </div>
      )}

      {/* ============ SECTION 2: KARAOKE BOOKING ============ */}
      {activeSection === 'karaoke' && (
        <div className="space-y-4">
          {/* Sub-section accordion buttons */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {[
              { id: 'reconciliation', label: 'Reconciliation', icon: TrendingUp, color: 'amber', badge: reconciliationData?.pending_count },
              { id: 'settlement', label: 'Monthly Settlement', icon: FileText, color: 'blue' },
              { id: 'yearly', label: 'Yearly Summary', icon: Calendar, color: 'indigo' },
            ].map(sec => (
              <button
                key={sec.id}
                onClick={() => setExpandedKaraokeSection(expandedKaraokeSection === sec.id ? '' : sec.id)}
                data-testid={`karaoke-section-${sec.id}`}
                className={`flex items-center gap-2 px-3 py-2.5 text-sm font-bold border-2 border-black transition-all ${
                  expandedKaraokeSection === sec.id
                    ? `bg-${sec.color}-600 text-white shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]`
                    : 'bg-white hover:bg-gray-50'
                }`}
              >
                <sec.icon size={16} />
                <span className="hidden sm:inline">{sec.label}</span>
                <span className="sm:hidden text-xs">{sec.label.split(' ')[0]}</span>
                {sec.badge > 0 && (
                  <span className="px-1.5 py-0.5 bg-red-500 text-white text-xs rounded-full ml-auto">{sec.badge}</span>
                )}
                {expandedKaraokeSection === sec.id ? <ChevronUp size={14} className="ml-auto" /> : <ChevronDown size={14} className="ml-auto" />}
              </button>
            ))}
          </div>


          {/* Pending Reconciliation */}
          {expandedKaraokeSection === 'reconciliation' && (
          <div className="brutal-card">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold flex items-center gap-2">
                <TrendingUp className="text-amber-600" /> Pending Reconciliation
                {reconciliationData?.pending_count > 0 && (
                  <span className="px-2 py-0.5 bg-amber-200 text-amber-800 text-xs font-bold rounded-full">
                    {reconciliationData.pending_count}
                  </span>
                )}
              </h3>
              <button onClick={fetchReconciliation} disabled={loadingRecon}
                className="px-3 py-1 text-sm bg-amber-500 text-white font-bold border-2 border-black hover:bg-amber-600"
                data-testid="recon-refresh-btn">
                {loadingRecon ? 'Loading...' : 'Refresh'}
              </button>
            </div>

            {reconciliationData && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="p-3 bg-green-50 border-2 border-green-300 text-center">
                    <p className="text-xs text-green-600">Top-ups Due</p>
                    <p className="text-xl font-bold text-green-700">${reconciliationData.total_topups_due}</p>
                  </div>
                  <div className="p-3 bg-red-50 border-2 border-red-300 text-center">
                    <p className="text-xs text-red-600">Refunds Due</p>
                    <p className="text-xl font-bold text-red-700">${reconciliationData.total_refunds_due}</p>
                  </div>
                  <div className="p-3 bg-amber-50 border-2 border-amber-300 text-center">
                    <p className="text-xs text-amber-600">Pending Items</p>
                    <p className="text-xl font-bold text-amber-700">{reconciliationData.pending_count}</p>
                  </div>
                </div>

                {reconciliationData.items?.length > 0 && (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-amber-100 border-b-2 border-black">
                          <th className="p-2 text-left">Code</th>
                          <th className="p-2 text-left">Name</th>
                          <th className="p-2 text-left">Date</th>
                          <th className="p-2 text-right">Original</th>
                          <th className="p-2 text-right">New Price</th>
                          <th className="p-2 text-right">Difference</th>
                          <th className="p-2 text-left">Type</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reconciliationData.items.map((item, idx) => (
                          <tr key={idx} className="border-b hover:bg-amber-50">
                            <td className="p-2 font-mono">{item.booking_code}</td>
                            <td className="p-2">{item.member_name}</td>
                            <td className="p-2 font-mono">{formatDate(item.date)}</td>
                            <td className="p-2 text-right">${item.original_price}</td>
                            <td className="p-2 text-right font-bold">${item.new_price}</td>
                            <td className={`p-2 text-right font-bold ${item.price_difference > 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {item.price_difference > 0 ? '+' : ''}${item.price_difference}
                            </td>
                            <td className="p-2">
                              <span className={`px-2 py-0.5 text-xs font-bold rounded ${
                                item.reconciliation_type === 'top-up' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                              }`}>{item.reconciliation_type}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {reconciliationData.pending_count === 0 && (
                  <p className="text-center text-gray-500 py-4">No pending reconciliation items</p>
                )}
              </div>
            )}

            {!reconciliationData && !loadingRecon && (
              <p className="text-center text-gray-500 py-8">Click &quot;Refresh&quot; to load reconciliation data</p>
            )}
          </div>
          )}

          {/* Monthly Settlement Report */}
          {expandedKaraokeSection === 'settlement' && (
          <div className="brutal-card bg-gradient-to-r from-blue-50 to-sky-50">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
              <h3 className="text-lg font-bold flex items-center gap-2">
                <FileText className="text-blue-600" /> Monthly Settlement Report
              </h3>
              <div className="flex items-center gap-2 flex-wrap">
                <select value={settlementMonth} onChange={(e) => setSettlementMonth(parseInt(e.target.value))}
                  className="border-2 border-black px-3 py-2 font-bold" data-testid="settlement-month-select">
                  {monthShort.map((m, i) => (
                    <option key={i} value={i + 1}>{m}</option>
                  ))}
                </select>
                <select value={settlementYear} onChange={(e) => setSettlementYear(parseInt(e.target.value))}
                  className="border-2 border-black px-3 py-2 font-bold" data-testid="settlement-year-select">
                  {[2024, 2025, 2026, 2027].map(y => (<option key={y} value={y}>{y}</option>))}
                </select>
                <select value={settlementKj} onChange={(e) => setSettlementKj(e.target.value)}
                  className="border-2 border-black px-3 py-2 font-bold" data-testid="settlement-kj-select">
                  <option value="">All KJs</option>
                  {kjList.map(kj => (<option key={kj} value={kj}>{kj}</option>))}
                </select>
                <button onClick={fetchSettlement} disabled={loadingSettlement}
                  className="px-4 py-2 bg-blue-600 text-white font-bold border-2 border-black hover:bg-blue-700"
                  data-testid="settlement-generate-btn">
                  {loadingSettlement ? 'Loading...' : 'Generate'}
                </button>
              </div>
            </div>

            {settlementData && (
              <div className="space-y-4">
                {/* Summary Cards */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="p-3 bg-white border-2 border-blue-300 text-center">
                    <p className="text-xs text-blue-600 font-medium">Gross Collections</p>
                    <p className="text-xl font-bold text-blue-700">${settlementData.summary.total_gross}</p>
                    <p className="text-xs text-gray-500">{settlementData.summary.total_bookings} bookings</p>
                  </div>
                  <div className="p-3 bg-white border-2 border-orange-300 text-center">
                    <p className="text-xs text-orange-600 font-medium">Modification Refunds</p>
                    <p className="text-xl font-bold text-orange-600">-${settlementData.summary.total_refunds || 0}</p>
                  </div>
                  <div className="p-3 bg-white border-2 border-red-300 text-center">
                    <p className="text-xs text-red-600 font-medium">Cancellation Refunds</p>
                    <p className="text-xl font-bold text-red-600">-${settlementData.summary.total_cancellation_refunds || 0}</p>
                    <p className="text-xs text-gray-500">{settlementData.summary.total_cancelled || 0} cancelled</p>
                  </div>
                  <div className="p-3 bg-green-50 border-2 border-green-400 text-center">
                    <p className="text-xs text-green-600 font-medium">Net Revenue</p>
                    <p className="text-xl font-bold text-green-700">${settlementData.summary.net_revenue}</p>
                  </div>
                </div>

                {/* Transaction Table */}
                {settlementData.transactions.length > 0 ? (
                  <div className="overflow-x-auto">
                    <h4 className="font-bold text-sm mb-2">
                      Transaction Details - {settlementData.month_name} {settlementData.year}
                      {settlementKj && <span className="text-blue-600 ml-2">(KJ: {settlementKj})</span>}
                    </h4>
                    <table className="w-full min-w-[800px] text-sm">
                      <thead>
                        <tr className="bg-blue-100 border-b-2 border-black">
                          <th className="p-2 text-left">Date</th>
                          <th className="p-2 text-left">Resident</th>
                          <th className="p-2 text-left">KJ</th>
                          <th className="p-2 text-left">Code</th>
                          <th className="p-2 text-left">Slot</th>
                          <th className="p-2 text-right">Amount</th>
                          <th className="p-2 text-right">Adj.</th>
                          <th className="p-2 text-left">onePA Txn#</th>
                        </tr>
                      </thead>
                      <tbody>
                        {settlementData.transactions.map((t, idx) => (
                          <tr key={idx} className="border-b hover:bg-blue-50">
                            <td className="p-2 font-mono">{formatDate(t.date)}</td>
                            <td className="p-2 font-bold">{t.resident_name || t.kj_name}</td>
                            <td className="p-2">{t.kj_name || '-'}</td>
                            <td className="p-2 font-mono">{t.booking_code}</td>
                            <td className="p-2 text-xs">{t.slot_type === 'afternoon' ? '1PM-5PM' : '6PM-10PM'}</td>
                            <td className="p-2 text-right font-bold text-green-600">${t.final_amount}</td>
                            <td className="p-2 text-right">
                              {t.price_difference > 0 ? (
                                <span className="text-blue-600 font-bold">+${t.price_difference}</span>
                              ) : t.price_difference < 0 ? (
                                <span className="text-red-600 font-bold">${t.price_difference}</span>
                              ) : '-'}
                            </td>
                            <td className="p-2">
                              {t.transaction_number ? (
                                <span className="font-mono text-xs bg-blue-50 text-blue-700 px-1 py-0.5 rounded">{t.transaction_number}</span>
                              ) : <span className="text-xs text-gray-400">-</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-center text-gray-500 py-4">No verified transactions for this period</p>
                )}

                {/* Cancellation/Refund Table */}
                {settlementData.refund_transactions && settlementData.refund_transactions.length > 0 && (
                  <div>
                    <h4 className="font-bold text-sm text-red-600 mb-2">Cancellations / Full Refunds</h4>
                    <div className="overflow-x-auto">
                      <table className="w-full min-w-[600px] text-sm">
                        <thead>
                          <tr className="bg-red-100 border-b-2 border-red-400">
                            <th className="p-2 text-left">Date</th>
                            <th className="p-2 text-left">Resident</th>
                            <th className="p-2 text-left">KJ</th>
                            <th className="p-2 text-left">Code</th>
                            <th className="p-2 text-left">Slot</th>
                            <th className="p-2 text-right">Original</th>
                            <th className="p-2 text-right">Refund</th>
                          </tr>
                        </thead>
                        <tbody>
                          {settlementData.refund_transactions.map((r, idx) => (
                            <tr key={idx} className="border-b hover:bg-red-50">
                              <td className="p-2 font-mono">{formatDate(r.date)}</td>
                              <td className="p-2 font-bold">{r.resident_name || r.kj_name}</td>
                              <td className="p-2">{r.kj_name || '-'}</td>
                              <td className="p-2 font-mono">{r.booking_code}</td>
                              <td className="p-2 text-xs">{r.slot_type === 'afternoon' ? '1PM-5PM' : '6PM-10PM'}</td>
                              <td className="p-2 text-right">${r.original_price}</td>
                              <td className="p-2 text-right font-bold text-red-600">-${r.refund_amount}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                <button onClick={generateSettlementPDF}
                  className="w-full sm:w-auto px-6 py-3 bg-blue-600 text-white font-bold border-2 border-black hover:bg-blue-700 flex items-center justify-center gap-2"
                  data-testid="settlement-pdf-btn">
                  <Download size={18} /> EXPORT SETTLEMENT PDF
                </button>
              </div>
            )}

            {!settlementData && (
              <p className="text-center text-gray-500 py-8">Select month/year and click &quot;Generate&quot; to view settlement report</p>
            )}
          </div>
          )}

          {/* Yearly Summary */}
          {expandedKaraokeSection === 'yearly' && (
          <div className="brutal-card bg-gradient-to-r from-indigo-50 to-violet-50">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
              <h3 className="text-lg font-bold flex items-center gap-2">
                <Calendar className="text-indigo-600" /> Karaoke Yearly Summary
              </h3>
              <div className="flex items-center gap-2">
                <select value={yearlySummaryYear} onChange={(e) => setYearlySummaryYear(parseInt(e.target.value))}
                  className="border-2 border-black px-3 py-2 font-bold" data-testid="yearly-year-select">
                  {[2024, 2025, 2026, 2027].map(y => (<option key={y} value={y}>{y}</option>))}
                </select>
                <button onClick={fetchYearlySummary} disabled={loadingYearly}
                  className="px-4 py-2 bg-indigo-600 text-white font-bold border-2 border-black hover:bg-indigo-700"
                  data-testid="yearly-generate-btn">
                  {loadingYearly ? 'Loading...' : 'Load'}
                </button>
              </div>
            </div>

            {yearlyData && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="p-3 bg-white border-2 border-indigo-300 text-center">
                    <p className="text-xs text-indigo-600 font-medium">Yearly Gross</p>
                    <p className="text-xl font-bold text-indigo-700">${yearlyData.total_gross}</p>
                  </div>
                  <div className="p-3 bg-white border-2 border-red-300 text-center">
                    <p className="text-xs text-red-600 font-medium">Yearly Refunds</p>
                    <p className="text-xl font-bold text-red-600">${yearlyData.total_refunds}</p>
                  </div>
                  <div className="p-3 bg-green-50 border-2 border-green-400 text-center">
                    <p className="text-xs text-green-600 font-medium">Net Revenue</p>
                    <p className="text-xl font-bold text-green-700">${yearlyData.net_revenue}</p>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full min-w-[500px] text-sm">
                    <thead>
                      <tr className="bg-indigo-100 border-b-2 border-black">
                        <th className="p-2 text-left">Month</th>
                        <th className="p-2 text-center">Bookings</th>
                        <th className="p-2 text-right">Gross</th>
                        <th className="p-2 text-right">Refunds</th>
                        <th className="p-2 text-right">Net</th>
                      </tr>
                    </thead>
                    <tbody>
                      {yearlyData.monthly_breakdown.map((month, idx) => (
                        <tr key={idx} className={`border-b ${month.bookings_count > 0 ? 'hover:bg-indigo-50' : 'text-gray-400'}`}>
                          <td className="p-2 font-bold">{month.month_name}</td>
                          <td className="p-2 text-center font-mono">{month.bookings_count}</td>
                          <td className="p-2 text-right font-mono">${month.gross}</td>
                          <td className="p-2 text-right font-mono text-red-600">${month.refunds}</td>
                          <td className="p-2 text-right font-mono font-bold text-green-600">${month.net}</td>
                        </tr>
                      ))}
                      <tr className="bg-indigo-100 border-t-2 border-indigo-400 font-bold">
                        <td className="p-2">TOTAL</td>
                        <td className="p-2 text-center">{yearlyData.monthly_breakdown.reduce((s, m) => s + m.bookings_count, 0)}</td>
                        <td className="p-2 text-right">${yearlyData.total_gross}</td>
                        <td className="p-2 text-right text-red-600">${yearlyData.total_refunds}</td>
                        <td className="p-2 text-right text-green-700">${yearlyData.net_revenue}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {!yearlyData && (
              <p className="text-center text-gray-500 py-8">Select year and click &quot;Load&quot; to view yearly karaoke summary</p>
            )}
          </div>
          )}
        </div>
      )}

      {/* ============ SECTION 3: BBQ PIT BOOKING ============ */}
      {activeSection === 'bbq' && (
        <div className="space-y-6">
          {/* BBQ Monthly Income */}
          <div className="brutal-card bg-gradient-to-r from-orange-50 to-amber-50">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
              <h3 className="text-lg font-bold flex items-center gap-2">
                <Flame className="text-orange-600" /> BBQ Pit Booking Monthly Income
              </h3>
              <div className="flex items-center gap-2 flex-wrap">
                <select value={bbqReportMonth} onChange={(e) => setBbqReportMonth(parseInt(e.target.value))}
                  className="border-2 border-black px-3 py-2 font-bold" data-testid="bbq-month-select">
                  {monthShort.map((m, i) => (
                    <option key={i} value={i + 1}>{m}</option>
                  ))}
                </select>
                <select value={bbqReportYear} onChange={(e) => setBbqReportYear(parseInt(e.target.value))}
                  className="border-2 border-black px-3 py-2 font-bold" data-testid="bbq-year-select">
                  {[2024, 2025, 2026, 2027].map(y => (<option key={y} value={y}>{y}</option>))}
                </select>
                <button onClick={fetchBbqReport} disabled={loadingBbq}
                  className="px-4 py-2 bg-orange-600 text-white font-bold border-2 border-black hover:bg-orange-700"
                  data-testid="bbq-generate-btn">
                  {loadingBbq ? 'Loading...' : 'Generate'}
                </button>
              </div>
            </div>

            {bbqReport && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="p-4 bg-white border-2 border-orange-300 text-center">
                    <p className="text-xs text-orange-600 font-medium">Total BBQ Revenue</p>
                    <p className="text-2xl font-bold text-orange-700">${bbqReport.total.toFixed(2)}</p>
                  </div>
                  <div className="p-4 bg-white border-2 border-orange-300 text-center">
                    <p className="text-xs text-orange-600 font-medium">Completed Bookings</p>
                    <p className="text-2xl font-bold text-orange-700">{bbqReport.count}</p>
                  </div>
                </div>

                {bbqReport.bookings.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[700px] text-sm">
                      <thead>
                        <tr className="bg-orange-100 border-b-2 border-black">
                          <th className="p-2 text-left">Date</th>
                          <th className="p-2 text-left">Code</th>
                          <th className="p-2 text-left">Name</th>
                          <th className="p-2 text-left">Pit</th>
                          <th className="p-2 text-left">Slot</th>
                          <th className="p-2 text-right">Amount</th>
                          <th className="p-2 text-left">Bank Ref.</th>
                        </tr>
                      </thead>
                      <tbody>
                        {bbqReport.bookings.map((b, idx) => (
                          <tr key={idx} className="border-b hover:bg-orange-50">
                            <td className="p-2 font-mono">{formatDate(b.date)}</td>
                            <td className="p-2 font-mono">{b.booking_code}</td>
                            <td className="p-2">{b.name}</td>
                            <td className="p-2">{b.pit_selection === 'both' ? 'Both Pits' : b.pit_selection === 'pit1' ? 'Pit 1' : b.pit_selection === 'pit2' ? 'Pit 2' : b.pit_number || '-'}</td>
                            <td className="p-2 text-xs">{b.slot_type === 'afternoon' ? '1PM-5PM' : '6PM-10PM'}</td>
                            <td className="p-2 text-right font-bold">${Number(b.price || 0).toFixed(2)}</td>
                            <td className="p-2">
                              {b.bank_transfer_reference ? (
                                <span className="font-mono text-xs bg-orange-50 text-orange-700 px-1 py-0.5 rounded">{b.bank_transfer_reference}</span>
                              ) : <span className="text-xs text-gray-400">-</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-center text-gray-500 py-4">No completed BBQ bookings for this period</p>
                )}

                <button onClick={generateBbqPDF}
                  className="w-full sm:w-auto px-6 py-3 bg-orange-600 text-white font-bold border-2 border-black hover:bg-orange-700 flex items-center justify-center gap-2"
                  data-testid="bbq-pdf-btn">
                  <Download size={18} /> EXPORT BBQ INCOME PDF
                </button>
              </div>
            )}

            {!bbqReport && (
              <p className="text-center text-gray-500 py-8">Select month/year and click &quot;Generate&quot; to view BBQ income report</p>
            )}
          </div>

          {/* BBQ Yearly Summary */}
          <div className="brutal-card bg-gradient-to-r from-amber-50 to-yellow-50">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
              <h3 className="text-lg font-bold flex items-center gap-2">
                <Calendar className="text-amber-700" /> BBQ Pit Booking Yearly Summary
              </h3>
              <div className="flex items-center gap-2">
                <select value={bbqYearlyYear} onChange={(e) => setBbqYearlyYear(parseInt(e.target.value))}
                  className="border-2 border-black px-3 py-2 font-bold" data-testid="bbq-yearly-year-select">
                  {[2024, 2025, 2026, 2027].map(y => (<option key={y} value={y}>{y}</option>))}
                </select>
                <button onClick={fetchBbqYearlySummary} disabled={loadingBbqYearly}
                  className="px-4 py-2 bg-amber-600 text-white font-bold border-2 border-black hover:bg-amber-700"
                  data-testid="bbq-yearly-generate-btn">
                  {loadingBbqYearly ? 'Loading...' : 'Load'}
                </button>
              </div>
            </div>

            {bbqYearlyData && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="p-3 bg-white border-2 border-amber-300 text-center">
                    <p className="text-xs text-amber-700 font-medium">Yearly Revenue</p>
                    <p className="text-xl font-bold text-amber-800">${bbqYearlyData.total_revenue}</p>
                  </div>
                  <div className="p-3 bg-white border-2 border-amber-300 text-center">
                    <p className="text-xs text-amber-700 font-medium">Total Bookings</p>
                    <p className="text-xl font-bold text-amber-800">{bbqYearlyData.total_bookings}</p>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full min-w-[400px] text-sm">
                    <thead>
                      <tr className="bg-amber-100 border-b-2 border-black">
                        <th className="p-2 text-left">Month</th>
                        <th className="p-2 text-center">Bookings</th>
                        <th className="p-2 text-right">Revenue</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bbqYearlyData.monthly_breakdown.map((month, idx) => (
                        <tr key={idx} className={`border-b ${month.bookings_count > 0 ? 'hover:bg-amber-50' : 'text-gray-400'}`}>
                          <td className="p-2 font-bold">{month.month_name}</td>
                          <td className="p-2 text-center font-mono">{month.bookings_count}</td>
                          <td className="p-2 text-right font-mono font-bold text-amber-700">${month.revenue}</td>
                        </tr>
                      ))}
                      <tr className="bg-amber-100 border-t-2 border-amber-400 font-bold">
                        <td className="p-2">TOTAL</td>
                        <td className="p-2 text-center">{bbqYearlyData.total_bookings}</td>
                        <td className="p-2 text-right text-amber-800">${bbqYearlyData.total_revenue}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {!bbqYearlyData && (
              <p className="text-center text-gray-500 py-8">Select year and click &quot;Load&quot; to view yearly BBQ summary</p>
            )}
          </div>
        </div>
      )}

      {/* ============ SECTION: OUTSTANDING PAYMENTS ============ */}
      {activeSection === 'outstanding' && (
        <div className="space-y-6">
          <div className="brutal-card">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold">Outstanding Payments</h3>
              <div className="flex gap-2">
                <button
                  onClick={fetchOutstanding}
                  disabled={loadingOutstanding}
                  className="brutal-btn-primary px-4 py-2 text-sm"
                  data-testid="outstanding-refresh-btn"
                >
                  {loadingOutstanding ? 'Loading...' : 'Refresh'}
                </button>
                {outstandingData && outstandingData.entries.length > 0 && (
                  <button
                    onClick={() => {
                      try {
                        generateOutstandingPaymentsPDF(outstandingData);
                        toast.success("Outstanding Payments PDF downloaded");
                      } catch { toast.error("Failed to generate PDF"); }
                    }}
                    className="flex items-center gap-2 px-4 py-2 bg-red-700 text-white font-bold text-sm border-2 border-black hover:bg-red-800"
                    data-testid="outstanding-download-pdf-btn"
                  >
                    <Download size={16} /> Download PDF
                  </button>
                )}
              </div>
            </div>

            {outstandingData && (
              <>
                {/* Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-testid="outstanding-summary">
                  <div className="p-4 border-2 border-black bg-amber-50">
                    <p className="text-xs font-bold text-amber-800 uppercase">Karaoke Pending</p>
                    <p className="text-2xl font-bold text-amber-700 mt-1">{outstandingData.summary.karaoke_pending}</p>
                  </div>
                  <div className="p-4 border-2 border-black bg-red-50">
                    <p className="text-xs font-bold text-red-800 uppercase">Completed Unpaid</p>
                    <p className="text-2xl font-bold text-red-700 mt-1">{outstandingData.summary.karaoke_completed_unpaid}</p>
                  </div>
                  <div className="p-4 border-2 border-black bg-orange-50">
                    <p className="text-xs font-bold text-orange-800 uppercase">BBQ Pending</p>
                    <p className="text-2xl font-bold text-orange-700 mt-1">{outstandingData.summary.bbq_pending}</p>
                  </div>
                  <div className="p-4 border-2 border-black bg-red-100">
                    <p className="text-xs font-bold text-red-800 uppercase">Total Outstanding</p>
                    <p className="text-2xl font-bold text-red-700 mt-1" data-testid="outstanding-total">${outstandingData.summary.total_outstanding}</p>
                  </div>
                </div>

                {/* Table */}
                {outstandingData.entries.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm border-collapse" data-testid="outstanding-table">
                      <thead>
                        <tr className="bg-red-700 text-white">
                          <th className="p-2 text-left border border-red-600">Facility</th>
                          <th className="p-2 text-left border border-red-600">Category</th>
                          <th className="p-2 text-left border border-red-600">Code</th>
                          <th className="p-2 text-left border border-red-600">Name</th>
                          <th className="p-2 text-left border border-red-600">Contact</th>
                          <th className="p-2 text-left border border-red-600">Date</th>
                          <th className="p-2 text-left border border-red-600">Slot</th>
                          <th className="p-2 text-right border border-red-600">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {outstandingData.entries.map((e, i) => (
                          <tr key={i} className={`border-b ${e.category === 'Completed - Unpaid' ? 'bg-red-50' : 'bg-white'}`}>
                            <td className="p-2 border text-xs">
                              <span className={`px-2 py-0.5 rounded font-bold ${e.facility === 'Karaoke' ? 'bg-purple-100 text-purple-700' : 'bg-orange-100 text-orange-700'}`}>
                                {e.facility}
                              </span>
                            </td>
                            <td className="p-2 border text-xs">
                              <span className={`px-2 py-0.5 rounded font-bold ${e.category === 'Completed - Unpaid' ? 'bg-red-200 text-red-800' : 'bg-amber-200 text-amber-800'}`}>
                                {e.category}
                              </span>
                            </td>
                            <td className="p-2 border font-mono text-xs font-bold">{e.booking_code}</td>
                            <td className="p-2 border text-xs font-bold">{e.name}</td>
                            <td className="p-2 border text-xs">
                              <div>{e.email}</div>
                              <div className="text-gray-500">{e.phone}</div>
                            </td>
                            <td className="p-2 border text-xs">{e.date}</td>
                            <td className="p-2 border text-xs">{e.slot}</td>
                            <td className="p-2 border text-right font-bold">${e.amount}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="bg-red-100 font-bold border-t-2 border-black">
                          <td colSpan={7} className="p-2 text-right border">TOTAL OUTSTANDING</td>
                          <td className="p-2 text-right border text-red-700">${outstandingData.summary.total_outstanding}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                ) : (
                  <p className="text-center text-green-600 font-bold py-8">No outstanding payments! All bookings are settled.</p>
                )}
              </>
            )}
            {!outstandingData && !loadingOutstanding && (
              <p className="text-center text-gray-500 py-8">Click &quot;Refresh&quot; to load outstanding payments</p>
            )}
          </div>
        </div>
      )}

      {/* ============ SECTION 4: P/L DASHBOARD ============ */}
      {activeSection === 'pl' && (
        <div className="space-y-6">
          {/* Monthly P/L Controls */}
          <div className="brutal-card">
            <div className="flex flex-wrap items-end gap-4 mb-6">
              <div>
                <label className="block text-xs font-bold mb-1">Month</label>
                <select value={plMonth} onChange={e => setPlMonth(Number(e.target.value))} className="p-2 border-2 border-black text-sm" data-testid="pl-month">
                  {monthNames.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-bold mb-1">Year</label>
                <select value={plYear} onChange={e => setPlYear(Number(e.target.value))} className="p-2 border-2 border-black text-sm" data-testid="pl-year">
                  {[2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
              <button onClick={fetchPl} disabled={loadingPl} className="brutal-btn-primary px-6 py-2 text-sm" data-testid="pl-fetch-btn">
                {loadingPl ? 'Loading...' : 'Load P/L'}
              </button>
              {plData && (
                <button
                  onClick={() => {
                    try {
                      generateProfitLossPDF(plData, plAnnual);
                      toast.success("P/L Report PDF downloaded");
                    } catch (err) {
                      console.error(err);
                      toast.error("Failed to generate PDF");
                    }
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-slate-800 text-white font-bold text-sm border-2 border-black hover:bg-slate-700"
                  data-testid="pl-download-pdf-btn"
                >
                  <Download size={16} /> Download PDF
                </button>
              )}
            </div>

            {plData && (
              <div className="space-y-6">
                {/* Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="pl-summary-cards">
                  <div className="p-4 border-2 border-black bg-green-50">
                    <p className="text-xs font-bold text-green-800 uppercase">Total Revenue</p>
                    <p className="text-2xl font-bold text-green-700 mt-1" data-testid="pl-total-revenue">${plData.revenue.total_revenue}</p>
                    <p className="text-xs text-gray-500 mt-1">Karaoke ${plData.revenue.karaoke_net} + BBQ ${plData.revenue.bbq_net}</p>
                  </div>
                  <div className="p-4 border-2 border-black bg-red-50">
                    <p className="text-xs font-bold text-red-800 uppercase">KJ Costs</p>
                    <p className="text-2xl font-bold text-red-700 mt-1" data-testid="pl-kj-cost">${plData.costs.total_kj_cost}</p>
                    <p className="text-xs text-gray-500 mt-1">{plData.costs.kj_sessions} session(s) @ $35</p>
                  </div>
                  <div className={`p-4 border-2 border-black ${plData.profit.net_profit >= 0 ? 'bg-blue-50' : 'bg-red-100'}`}>
                    <p className="text-xs font-bold text-blue-800 uppercase">Net Profit</p>
                    <p className={`text-2xl font-bold mt-1 ${plData.profit.net_profit >= 0 ? 'text-blue-700' : 'text-red-700'}`} data-testid="pl-net-profit">
                      ${plData.profit.net_profit}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">Revenue - KJ Costs</p>
                  </div>
                  <div className="p-4 border-2 border-black bg-amber-50">
                    <p className="text-xs font-bold text-amber-800 uppercase">Community Subsidy</p>
                    <p className="text-2xl font-bold text-amber-700 mt-1" data-testid="pl-subsidy">${plData.subsidy.total_discounts}</p>
                    <p className="text-xs text-gray-500 mt-1">{plData.subsidy.discount_count} special booking(s)</p>
                  </div>
                </div>

                {/* Facility Breakdown */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-4 border-2 border-black bg-purple-50" data-testid="pl-karaoke-breakdown">
                    <p className="text-sm font-bold text-purple-800 mb-3">Karaoke Breakdown</p>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between"><span>Gross Revenue</span><span className="font-bold">${plData.revenue.karaoke_gross}</span></div>
                      <div className="flex justify-between text-amber-700"><span>Less: Discounts</span><span className="font-bold">-${plData.revenue.karaoke_discounts}</span></div>
                      <div className="flex justify-between border-t border-purple-300 pt-1"><span>Net Revenue</span><span className="font-bold">${plData.revenue.karaoke_net}</span></div>
                      <div className="flex justify-between text-red-700"><span>Less: KJ Costs</span><span className="font-bold">-${plData.costs.total_kj_cost}</span></div>
                      <div className="flex justify-between border-t-2 border-purple-500 pt-1 text-purple-800"><span className="font-bold">Karaoke Profit</span><span className="font-bold">${plData.profit.karaoke_profit}</span></div>
                    </div>
                  </div>
                  <div className="p-4 border-2 border-black bg-orange-50" data-testid="pl-bbq-breakdown">
                    <p className="text-sm font-bold text-orange-800 mb-3">BBQ Pit Breakdown</p>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between"><span>Revenue</span><span className="font-bold">${plData.revenue.bbq_net}</span></div>
                      <div className="flex justify-between text-gray-400"><span>Less: Costs</span><span className="font-bold">$0</span></div>
                      <div className="flex justify-between border-t-2 border-orange-500 pt-1 text-orange-800"><span className="font-bold">BBQ Profit</span><span className="font-bold">${plData.profit.bbq_profit}</span></div>
                    </div>
                    <p className="text-xs text-gray-500 mt-3 italic">{plData.bookings.bbq_count} booking(s) this month</p>

                    {/* BBQ Booking Detail */}
                    {plData.bookings.bbq_entries && plData.bookings.bbq_entries.length > 0 && (
                      <div className="mt-4 border-t border-orange-300 pt-3">
                        <p className="text-xs font-bold text-orange-700 mb-2">Booking Detail</p>
                        <div className="overflow-x-auto max-h-48 overflow-y-auto border border-orange-200 rounded">
                          <table className="w-full text-xs">
                            <thead className="bg-orange-100 sticky top-0">
                              <tr>
                                <th className="p-1.5 text-left font-bold">Code</th>
                                <th className="p-1.5 text-left font-bold">Name</th>
                                <th className="p-1.5 text-left font-bold">Date</th>
                                <th className="p-1.5 text-left font-bold">Pit</th>
                                <th className="p-1.5 text-right font-bold">Amount</th>
                              </tr>
                            </thead>
                            <tbody>
                              {plData.bookings.bbq_entries.map((e, i) => (
                                <tr key={i} className="border-t border-orange-100 hover:bg-orange-50/50">
                                  <td className="p-1.5 font-mono text-orange-600">{e.booking_code}</td>
                                  <td className="p-1.5">{e.name}</td>
                                  <td className="p-1.5">{e.date} {e.slot_type === 'afternoon' ? '1PM' : '6PM'}</td>
                                  <td className="p-1.5">{e.pit_selection}</td>
                                  <td className="p-1.5 text-right font-bold">${e.price}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* KJ Margin Indicator */}
                <div className="p-4 border-2 border-black bg-slate-50" data-testid="pl-kj-margin">
                  <p className="text-sm font-bold mb-2">KJ Cost Margin (Karaoke)</p>
                  <div className="flex items-center gap-4">
                    <div className="flex-1 bg-gray-200 h-6 border border-black overflow-hidden">
                      <div
                        className={`h-full transition-all ${plData.profit.kj_margin_pct > 50 ? 'bg-red-500' : plData.profit.kj_margin_pct > 30 ? 'bg-amber-500' : 'bg-green-500'}`}
                        style={{ width: `${Math.min(plData.profit.kj_margin_pct, 100)}%` }}
                      />
                    </div>
                    <span className="font-bold text-lg w-16 text-right" data-testid="pl-margin-pct">{plData.profit.kj_margin_pct}%</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {plData.profit.kj_margin_pct <= 30 ? 'Healthy margin' : plData.profit.kj_margin_pct <= 50 ? 'Moderate — monitor costs' : 'High — review KJ allocation'}
                  </p>
                </div>

                {/* Booking Type Comparison */}
                <div className="p-4 border-2 border-black" data-testid="pl-booking-types">
                  <p className="text-sm font-bold mb-3">Booking Type Comparison</p>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="text-center p-3 bg-blue-50 border border-blue-200">
                      <p className="text-3xl font-bold text-blue-700">{plData.bookings.standard_count}</p>
                      <p className="text-xs font-bold text-blue-600 mt-1">STANDARD</p>
                      <p className="text-sm text-gray-600">${plData.bookings.standard_revenue}</p>
                    </div>
                    <div className="text-center p-3 bg-amber-50 border border-amber-200">
                      <p className="text-3xl font-bold text-amber-700">{plData.bookings.special_count}</p>
                      <p className="text-xs font-bold text-amber-600 mt-1">SPECIAL / DISCOUNTED</p>
                      <p className="text-sm text-gray-600">${plData.bookings.special_revenue}</p>
                    </div>
                  </div>
                  {(plData.bookings.standard_count + plData.bookings.special_count) > 0 && (
                    <div className="mt-3 flex h-4 border border-black overflow-hidden">
                      <div className="bg-blue-500 transition-all" style={{ width: `${(plData.bookings.standard_count / (plData.bookings.standard_count + plData.bookings.special_count)) * 100}%` }} />
                      <div className="bg-amber-500 transition-all" style={{ width: `${(plData.bookings.special_count / (plData.bookings.standard_count + plData.bookings.special_count)) * 100}%` }} />
                    </div>
                  )}
                </div>

                {/* KJ Cost Detail Table with Filter */}
                {plData.costs.kj_cost_entries && plData.costs.kj_cost_entries.length > 0 && (
                  <KJCostDetailTable entries={plData.costs.kj_cost_entries} />
                )}
              </div>
            )}
            {!plData && !loadingPl && (
              <p className="text-center text-gray-500 py-8">Select month/year and click &quot;Load P/L&quot; to view</p>
            )}
          </div>

          {/* Annual P/L Summary Table */}
          <div className="brutal-card">
            <h3 className="text-lg font-bold mb-4">Annual P/L Summary</h3>
            <div className="flex items-end gap-4 mb-4">
              <div>
                <label className="block text-xs font-bold mb-1">Year</label>
                <select value={plAnnualYear} onChange={e => setPlAnnualYear(Number(e.target.value))} className="p-2 border-2 border-black text-sm" data-testid="pl-annual-year">
                  {[2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
              <button onClick={fetchPlAnnual} disabled={loadingPlAnnual} className="brutal-btn-primary px-6 py-2 text-sm" data-testid="pl-annual-fetch-btn">
                {loadingPlAnnual ? 'Loading...' : 'Load'}
              </button>
            </div>

            {plAnnual && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse" data-testid="pl-annual-table">
                  <thead>
                    <tr className="bg-slate-800 text-white">
                      <th className="p-2 text-left border border-slate-700">Month</th>
                      <th className="p-2 text-right border border-slate-700">Karaoke</th>
                      <th className="p-2 text-right border border-slate-700">BBQ</th>
                      <th className="p-2 text-right border border-slate-700">Revenue</th>
                      <th className="p-2 text-right border border-slate-700">KJ Cost</th>
                      <th className="p-2 text-right border border-slate-700">Discounts</th>
                      <th className="p-2 text-right border border-slate-700">Net Profit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {plAnnual.monthly.map(r => (
                      <tr key={r.month} className={`border-b ${r.total_revenue > 0 ? 'bg-white' : 'bg-gray-50 text-gray-400'}`}>
                        <td className="p-2 font-bold border">{r.month_name}</td>
                        <td className="p-2 text-right border">${r.karaoke_revenue}</td>
                        <td className="p-2 text-right border">${r.bbq_revenue}</td>
                        <td className="p-2 text-right border font-bold">${r.total_revenue}</td>
                        <td className="p-2 text-right border text-red-600">{r.kj_cost > 0 ? `-$${r.kj_cost}` : '-'}</td>
                        <td className="p-2 text-right border text-amber-600">{r.discounts > 0 ? `$${r.discounts}` : '-'}</td>
                        <td className={`p-2 text-right border font-bold ${r.net_profit >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                          ${r.net_profit}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-slate-100 font-bold border-t-2 border-black">
                      <td className="p-2 border">TOTAL</td>
                      <td className="p-2 text-right border">${plAnnual.totals.total_revenue - (plAnnual.monthly.reduce((s, r) => s + r.bbq_revenue, 0))}</td>
                      <td className="p-2 text-right border">${plAnnual.monthly.reduce((s, r) => s + r.bbq_revenue, 0)}</td>
                      <td className="p-2 text-right border">${plAnnual.totals.total_revenue}</td>
                      <td className="p-2 text-right border text-red-600">-${plAnnual.totals.total_kj_cost}</td>
                      <td className="p-2 text-right border text-amber-600">${plAnnual.totals.total_discounts}</td>
                      <td className={`p-2 text-right border ${plAnnual.totals.total_profit >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                        ${plAnnual.totals.total_profit}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
            {!plAnnual && !loadingPlAnnual && (
              <p className="text-center text-gray-500 py-8">Select year and click &quot;Load&quot; to view annual P/L</p>
            )}
          </div>
        </div>
      )}

      {/* ============ SECTION: CUSTOMER SUMMARY ============ */}
      {activeSection === 'customers' && (
        <CustomerSummarySection
          data={customerData}
          loading={loadingCustomers}
          onRefresh={fetchCustomerSummary}
        />
      )}

      {/* Audit Journal Link - always visible at bottom */}
      <button onClick={() => navigate('/admin/audit-journal')}
        className="w-full p-4 border-2 border-black font-bold text-left bg-white hover:bg-blue-50 transition-all flex items-center gap-3"
        data-testid="audit-journal-link">
        <ClipboardList size={24} className="text-blue-600" />
        <div>
          <div className="text-sm">Audit Journal</div>
          <div className="text-xs mt-1 text-gray-500">Action logs & history</div>
        </div>
      </button>
    </div>
  );
};

export default AdminFinanceTab;
